"use client";

import { useId, useState, type ReactNode, type Ref } from "react";
import { ACCOUNT_COLORS, ACCOUNT_EASING } from "./account-palette";

/**
 * Une ligne de saisie posée sur un filet, sans boîte.
 *
 * ## Pourquoi pas de boîte
 *
 * Avec deux champs et un bouton, un traitement encadré dessine trois
 * rectangles concurrents et le bouton perd toute saillance. Le filet n'en
 * dessine que deux traits et laisse le bouton comme unique forme pleine de
 * l'écran. C'est de la hiérarchie, pas de la décoration.
 *
 * On a écarté les deux extrêmes. *Aucun contour* échoue en produit : à vide,
 * rien ne distingue le champ d'un titre, et l'échappatoire habituelle — poser
 * le focus au montage — tombe précisément sur l'appareil qui compte ici, iOS
 * supprimant le focus programmatique sans geste utilisateur. *Encadré léger*
 * échoue autrement : sur fond sombre, un cadre sans fond lit comme un champ
 * **désactivé**, puisque nos champs actifs sont remplis d'un fond plus clair.
 *
 * ## Le filet est la ligne de statut
 *
 * Repos, focus et erreur passent tous par le même organe — un seul endroit à
 * regarder, au lieu d'une bordure qui change de teinte et d'un texte rouge qui
 * apparaît ailleurs. Et le changement d'état est visible à trois mètres, là où
 * une bordure de 1 px qui passe de 6 % à 30 % ne se voit pas.
 *
 * ## Ce que le dessin oblige à payer
 *
 * Un trait ne définit pas de cible : la ligne entière fait donc 44 px de haut
 * — le minimum tactile, pas 40 — et c'est le `<label>` qui la porte, pas
 * l'`<input>`. C'est le principal risque d'implémentation de ce composant, et
 * le point à vérifier en revue.
 */

type UnderlineFieldProps = {
  /**
   * Facultative. Sur un ecran ou le champ est seul, le seul texte de la page
   * apres le titre, l etiquette ne distingue plus rien : le placeholder dit
   * deja quoi ecrire, et la retirer laisse le champ respirer.
   */
  label?: string;
  value: string;
  onChange: (value: string) => void;
  type?: "email" | "text" | "password";
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  inputMode?: "email" | "text";
  /** Message d'erreur, ou texte d'aide quand il n'y a pas d'erreur. */
  hint?: string;
  error?: string | null;
  /** Rendu en bout de ligne — l'œil de révélation du mot de passe. */
  trailing?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  compact?: boolean;
  onBlur?: () => void;
  /**
   * Taille de la valeur saisie. 22 px dans une carte, davantage quand le champ
   * occupe une page entiere. Plafonnee par la largeur disponible : au-dela,
   * une adresse reelle de trente caracteres deborde et le champ se met a
   * defiler sous les doigts, ce qui annule tout le benefice.
   */
  fontSize?: number;
  /**
   * Alignement de la valeur. `center` sur une composition centrée en pleine
   * page, où une valeur alignée à gauche casse l'axe. La contrepartie est
   * réelle : une adresse plus longue que la ligne défile depuis le centre au
   * lieu de la fin, ce qui se voit davantage.
   */
  align?: "left" | "center";
};

export function UnderlineField({
  label,
  fontSize: fontSizeProp,
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  autoFocus,
  inputMode,
  hint,
  error = null,
  trailing,
  inputRef,
  compact = false,
  onBlur,
  align = "left",
}: UnderlineFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const [focused, setFocused] = useState(false);

  const hasError = error !== null && error !== "";
  const fontSize = fontSizeProp ?? (compact ? 19 : 22);
  // La ligne suit la taille du texte : a 26 px, 44 px ne laissent plus
  // respirer la valeur au-dessus du filet.
  const lineHeight = Math.max(44, Math.round(fontSize * 1.9));

  return (
    <div className="flex flex-col">
      {label !== undefined ? (
        <label
          htmlFor={id}
        // Casse normale, pas de `uppercase tracking-wider` : la majuscule
        // espacée est du langage de tableau de bord. En casse normale
        // l'étiquette devient une légende, et le rapport de 1 à 2 avec la
        // valeur fait tout le travail de hiérarchie.
          className="text-[11px] leading-[15px] font-medium select-none"
          style={{
            color: focused ? ACCOUNT_COLORS.textLabelActive : ACCOUNT_COLORS.textMuted,
            letterSpacing: "0.02em",
            transition: `color 220ms ${ACCOUNT_EASING}`,
          }}
        >
          {label}
        </label>
      ) : null}

      {/* La ligne : 44 px, cliquable sur toute sa largeur. Le texte est calé
          bas pour que le centre de gravité visuel soit près du filet, là où
          l'œil vise. */}
      <div className="relative flex items-end" style={{ height: lineHeight }}>
        <input
          id={id}
          ref={inputRef}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          inputMode={inputMode}
          aria-invalid={hasError || undefined}
          aria-describedby={hint !== undefined || hasError ? hintId : undefined}
          className="account-underline-input w-full bg-transparent border-0 outline-none p-0"
          style={{
            fontSize,
            lineHeight: `${Math.round(fontSize * 1.3)}px`,
            letterSpacing: "-0.01em",
            color: ACCOUNT_COLORS.text,
            paddingBottom: 10,
            paddingRight: trailing ? 40 : 0,
            paddingLeft: trailing && align === "center" ? 40 : 0,
            textAlign: align,
          }}
        />

        {trailing !== undefined && trailing !== null ? (
          <div className="absolute right-0 bottom-[6px] flex items-center">{trailing}</div>
        ) : null}

        {/* Filet au repos : toujours présent, c'est lui qui identifie le
            contrôle. */}
        <span
          aria-hidden
          className="absolute left-0 right-0 bottom-0"
          style={{ height: 1, backgroundColor: ACCOUNT_COLORS.rule }}
        />

        {/* Filet actif, superposé et déployé depuis la gauche. Un `scaleX`
            plutôt qu'une largeur animée : c'est une propriété composée, donc
            elle ne déclenche pas de recalcul de mise en page à chaque image. */}
        <span
          aria-hidden
          className="absolute left-0 right-0 bottom-0 origin-left"
          style={{
            height: 2,
            backgroundColor: hasError ? ACCOUNT_COLORS.error : ACCOUNT_COLORS.accent,
            // Pas de halo sur l'erreur : la lueur violette dit « ici, tout va
            // bien, continue », ce qui contredirait le message.
            boxShadow: hasError || !focused ? "none" : ACCOUNT_COLORS.ruleFocusGlow,
            transform: `scaleX(${focused || hasError ? 1 : 0})`,
            transition: `transform 220ms ${ACCOUNT_EASING}`,
          }}
        />
      </div>

      {hasError || hint !== undefined ? (
        // Une seule ligne réservée pour les deux : l'erreur occupe la place de
        // l'aide. La hauteur de la carte ne bouge donc pas entre l'état sain et
        // l'état en erreur, ce qui évite au focus de sauter sous le doigt.
        <p
          id={hintId}
          className="text-[11px] leading-[15px]"
          style={{
            marginTop: 10,
            textAlign: align,
            color: hasError ? ACCOUNT_COLORS.error : ACCOUNT_COLORS.textMuted,
          }}
          role={hasError ? "alert" : undefined}
        >
          {hasError ? error : hint}
        </p>
      ) : null}
    </div>
  );
}
